import type { FC, RefObject } from 'react';

import { Chip2D } from './variants/Chip2D';
import { Chip3D } from './variants/Chip3D';

export interface ChipProps {
  /** 100px if not specified. If `"inherit"` passed as size, takes size from `--chipSize` cascaded down. Make sure when setting inherit that this variable is in cascade. */
  size: number | 'inherit' | 'grow';
  /** Value of a chip. Is rounded to K, M, B, T, with maximum of two digits after decimal point. Uses [ChipValueFormatter](/?path=/docs/core-system-formatters--docs#chipvalueformatter) far value formatting */
  value: number;
  /** Color (denomination) of a chip [1, 8]. */
  weight?: number;
  /** Postfix, rendered after value, limited to characters available in `Montserat Chip` font */
  postfix?: string;
  /** Tilt of a chip, from 0 to 1. 1 being fully tilted. */
  tilt?: number;
  /** Rotation of a chip, in degrees. */
  rotate?: number;
}

export interface ChipViewProps extends ChipProps {
  weight: number;
  /** Color of a chip, by default fallbacks to default color for its weight */
  color?: string;
  /** Locale for formatting value */
  locale: string;
}

export interface Chip3DProps extends ChipViewProps {
  chipRefProp: RefObject<HTMLDivElement>;
  valueRefProp: RefObject<HTMLDivElement>;
}

/**
 * # Chip
 *
 * ### Chip component itself is **not intended to be used in game code**. It is used by `ChipConnected` component.
 *
 * Chip is a component used to represent a single chip. Can be configured to be of any size, has 8 weight (color) variants.
 *
 * Uses specific limited version of Montserrat font, called `Montserrat Chip`, that contains only digits and some characters,
 * including `K`, `M`, `B`, `T`, `+`, `.`, `,`.
 *
 * Value of a chip is dynamically scaled to fit in the chip using `ResizeObserver`.
 *
 * */
export const Chip: FC<ChipViewProps> = (props) => {
  const { tilt = 0, rotate = 0 } = props;

  if (tilt > 0 || rotate > 0) {
    return <Chip3D {...props} />;
  }

  return <Chip2D {...props} />;
};
